import { z } from 'zod';

/**
 * Legal library contracts (M3, ADR-012). These describe extracted Acts held in Firestore. Every
 * record is provisional: rights may be pending permission, and verification is per dimension.
 */

export const RightsStatusSchema = z.enum(['permitted', 'pending_permission', 'not_permitted']);
export type RightsStatus = z.infer<typeof RightsStatusSchema>;

export const ExtractionQualitySchema = z.enum(['structured', 'unstructured', 'scrambled']);

export const CatalogueActSchema = z.object({
  sourceId: z.string(),
  versionId: z.string(),
  title: z.string(),
  year: z.number().nullable(),
  quality: ExtractionQualitySchema,
  method: z.enum(['text-layer', 'ocr']),
  meanOcrConfidence: z.number().nullable(),
  sections: z.number(),
  passages: z.number(),
  warnings: z.number(),
  rightsStatus: RightsStatusSchema,
});
export type CatalogueAct = z.infer<typeof CatalogueActSchema>;

export const LibraryCatalogueSchema = z.object({
  updatedAt: z.string().nullable(),
  totals: z.object({
    uniqueFiles: z.number(),
    needingOcr: z.number(),
    uploaded: z.number(),
    ocr: z.number(),
    structured: z.number(),
    sections: z.number(),
  }),
  acts: z.array(CatalogueActSchema),
  /** True when pending-permission Acts were withheld by the rights gate (public deployment). */
  withheldByRightsGate: z.boolean(),
});
export type LibraryCatalogue = z.infer<typeof LibraryCatalogueSchema>;

const PendingVerdict = z.object({ verdict: z.string() });

export const LibraryPassageSchema = z.object({
  id: z.string(),
  label: z.string(),
  position: z.number(),
  kind: z.enum(['preamble', 'part', 'section', 'schedule', 'page']),
  number: z.string().nullable(),
  heading: z.string().nullable(),
  text: z.string(),
  pageStart: z.number(),
  pageEnd: z.number(),
});
export type LibraryPassage = z.infer<typeof LibraryPassageSchema>;

export const LibraryVersionSchema = z.object({
  id: z.string(),
  sourceId: z.string(),
  title: z.string(),
  year: z.number().nullable(),
  versionLabel: z.string(),
  contentHash: z.string(),
  rightsStatus: RightsStatusSchema,
  verification: z.object({
    authenticity: PendingVerdict,
    text_integrity: PendingVerdict,
    legal_status: PendingVerdict,
    legal_analysis: PendingVerdict,
  }),
  extraction: z.object({
    method: z.string(),
    quality: ExtractionQualitySchema,
    meanOcrConfidence: z.number().nullable().optional(),
    pageCount: z.number().nullable(),
    warnings: z.array(z.string()),
    gazette: z.string().nullable(),
    longTitle: z.string().nullable(),
  }),
  passages: z.array(LibraryPassageSchema),
});
export type LibraryVersion = z.infer<typeof LibraryVersionSchema>;

/** A judgment in the case catalogue (ADR-018), from SierraLII. */
export const CatalogueCaseSchema = z.object({
  sourceId: z.string(),
  versionId: z.string(),
  /** SierraLII's full title: parties, case number, citation and date. */
  title: z.string(),
  /** Neutral citation, e.g. "[1962] SLCA 1"; other citations of the same judgment follow. */
  citation: z.string().nullable(),
  aliases: z.array(z.string()),
  /** Court code (SLSC, SLCA, SLHC, SLHCLPED…). */
  court: z.string(),
  year: z.number().nullable(),
  decisionDate: z.string().nullable(),
  method: z.enum(['text-layer', 'ocr']),
  meanOcrConfidence: z.number().nullable(),
  pages: z.number(),
  /** The judgment's page on SierraLII, credited as the source. */
  url: z.string().nullable(),
  rightsStatus: RightsStatusSchema,
  /** Suggested legal areas (ADR-033): machine-suggested, not reviewed. */
  areas: z.array(z.string()).optional(),
  /** Suggested issues in a few plain words each (ADR-033): machine-suggested, not reviewed. */
  issues: z.array(z.string()).optional(),
  /** The model that suggested the labels. */
  labelledBy: z.string().optional(),
});
export type CatalogueCase = z.infer<typeof CatalogueCaseSchema>;

/**
 * Legal areas for finding judgments (ADR-033). A fixed list, so filters stay tidy; a judgment
 * may fall in several.
 */
export const LEGAL_AREAS = {
  land_property: 'Land and property',
  family: 'Family (marriage, divorce, custody, maintenance)',
  succession: 'Succession and estates',
  contract: 'Contract',
  tort: 'Tort (negligence, defamation, nuisance)',
  criminal_law: 'Criminal law',
  criminal_procedure: 'Criminal procedure (bail, trial, sentencing, appeals)',
  civil_procedure: 'Civil procedure and practice',
  evidence: 'Evidence',
  constitutional: 'Constitutional law and human rights',
  administrative: 'Administrative law and judicial review',
  employment: 'Employment and labour',
  company_commercial: 'Company, commercial and banking',
  elections: 'Elections',
  customary_law: 'Customary law',
} as const;
export type LegalArea = keyof typeof LEGAL_AREAS;

export const CaseCatalogueSchema = z.object({
  updatedAt: z.string().nullable(),
  cases: z.array(CatalogueCaseSchema),
  withheldByRightsGate: z.boolean(),
});
export type CaseCatalogue = z.infer<typeof CaseCatalogueSchema>;

/** A judgment found by searching the text of judgments (ADR-034), with where it matched. */
export const CaseSearchHitSchema = z.object({
  versionId: z.string(),
  title: z.string(),
  label: z.string(),
  pageStart: z.number(),
  excerpt: z.string(),
});
export type CaseSearchHit = z.infer<typeof CaseSearchHitSchema>;

export const CaseSearchSchema = z.object({
  query: z.string(),
  hits: z.array(CaseSearchHitSchema),
});
export type CaseSearch = z.infer<typeof CaseSearchSchema>;
